import Radio from "backbone.radio";
import $ from "jquery";
import { View } from "extensions/marionette";
import { getFeedback } from "extensions/sentry";

import { hashStringToColor } from "common/utils";
import { store } from "common/store";

import { pazienHistory } from "common/components/pazienHistory";
import { watch } from "common/components/watch";
import { CurrencyPicker } from "common/components/currencyPicker";

import { RootView as BusinessUnitPickerView } from "merchant/features/businessUnitPicker/businessUnitPicker";
import { STOP_MIMIC_USER } from "common/actions/authentication";

import { PackageInfo } from "./packageInfo";

import { CreateBuDropdown } from "./create-bu-dropdown";

import template from "./topBar.jst";
import { IconBar } from "./iconBar";

const ANIMATE_BUSINESS_UNIT_CONT = true;

const isMimicking = () => !!store.getState().authentication.mimic_as_user;

export const TopBar = View.extend({
    template,
    events: {
        "click .logout": "logout",
        "click .stopMimic": "stopMimic",
        "click a.gotofragment": "goToPage",
        "click .select-merchant": "showMerchantPicker",
        "click .expand-unit-menu, .selected-unit": "expandBusinessUnitDropdown"
    },
    modelEvents: {
        "change:username": "showUsername",
        "change:first_name": "showUsername",
        "change:avatar": "showAvatar"
    },
    ui: {
        username: ".username",
        avatar: ".profile-photo",
        merchant: ".parent-unit",
        entitySelector: "#businessUnitSelector"
    },
    
    regions: {
        packageInfo: ".package-info",
        currencySelector: ".currency-selector",
        iconbar: ".icon-menu",
        entitySelector: "#businessUnitSelector"
    },
    
    initialize() {
        this.listenTo(Radio.channel("main"), "changed", (props) => {
            this.showMerchantName(props);
            this.showBusinessUnits();
        });
    
        this._reduxSubscriptions = [
            store.subscribe(watch(store.getState, "common.bu_ref")((new_bu_ref) => {
                this.render();
            }))
        ];
        
        this.listenToKeyCombos();
    },
    
    renderPackageInfo() {
        // Return earlier if it's superuser on widgets without package override
        if (this.model.isSuperUser() && !localStorage.getItem("package-override")
            && store.getState().common.merchant_ref === store.getState().config.widgets_ref) return;
        
        this.showChildView(
            "packageInfo",
            <PackageInfo
                trialEndDate={this.model.activeMerchant.getRootMerchantProperty("trial_end_date")}
                merchantID={this.model.activeMerchant.id}
                merchantName={this.model.activeMerchant.get("display_name")}
                packageRef={this.model.activeMerchant.getRootMerchantProperty("package_ref")}
            />,
            {
                className: "hud",
                waitForAttach: true
            }
        );
    },
    
    listenToKeyCombos() {
        const hasMultipleMerchants = this.model.hasMultipleMerchants();
        
        $(document).on({
            "keydown.topbar": (e) => {
                if (e.which === 77 && e.altKey && hasMultipleMerchants) { // 77 is 'm'
                    this.showMerchantPicker();
                }
                if (e.which === 82 && e.altKey && e.shiftKey) { // 82 is 'r'
                    getFeedback();
                }
            }
        });
    },
    
    showBusinessUnits() {
        const { selectedNode, selectedTree } = this.model.getSelectedNodeWithTree();
        
        // If there are no units available for the user to pick then hide active unit and the ability to select
        // also account for a structure with one root and many units under that
        if (selectedTree.at(0).hasChildren()) {
            if (ANIMATE_BUSINESS_UNIT_CONT) {
                this.ui.entitySelector.fadeOut(0);
            }
            
            // If there is another this.entityView in the instance then destroy it first
            if (this.entityView) {
                this.entityView.destroy();
            }
            
            this.entityView = new BusinessUnitPickerView({
                model: selectedNode,
                available: selectedTree,
                el: this.ui.entitySelector[0]
            });
            this.entityView.render();
            
            if (ANIMATE_BUSINESS_UNIT_CONT) {
                this.ui.entitySelector.fadeIn(300);
            }
        } else if (this.hasBUManager()) {
            this.showChildView("entitySelector", <CreateBuDropdown />, {
                dumb: true
            });
        }
    },
    
    onRender() {
        if (!(store.getState().common.bu_ref || store.getState().common.merchant_ref)) return;
        // Don't render other top bar items in the admin app
        if (pazienHistory.isAdminApp()) return;
        
        this.showBusinessUnits();
        this.renderPackageInfo();
        this.showChildView("currencySelector", <CurrencyPicker />);
        this.showChildView("iconbar", <IconBar/>);
    },
    
    onAttach() {
        if (isMimicking()) {
            this.$el.closest("#topbar-wrapper").addClass("mimic-enabled");
        }
    },
    
    showUsername(model) {
        this.ui.username.text(model.getDisplayName());
        this.showAvatar();
    },
    showAvatar(model, avatar) {
        const displayName = this.model.getDisplayName();
        this.ui.avatar.css({
            backgroundColor: hashStringToColor(this.model.get("username")),
            backgroundImage: ""
        }).text(displayName[0]);
    },
    
    showMerchantName(props) {
        this.ui.merchant.html(`<div class="merchant-name">${props.display_name}</div>`);
    },
    
    getTopMerchantInfo() {
        try {
            const { selectedTree } = this.model.getSelectedNodeWithTree();
    
            return {
                display_name: selectedTree.at(0).get("display_name"),
                logo: selectedTree.at(0).get("logo")
            };
        } catch {
            return {};
        }
    },
    
    hasAlerts() {
        return Object.values(store.getState().common.user_permissions.alerts_view).some(Boolean);
    },
    
    hasBUManager() {
        return store.getState().common.user_permissions.has_bu_manager;
    },
    
    serializeData(...args) {
        const data = {
            ...this.serializeModel(),
            isAdminApp: pazienHistory.isAdminApp(),
            superuser: this.model.isSuperUser(),
            canAddUsers: store.getState().common.user_permissions.can_view_users,
            hasAlerts2: store.getState().user_merchant_settings.alerts_version === 2 && store.getState().common.user_permissions.feature_alerts,
            hasBUManager: this.hasBUManager(),
            logoutPage: "#logout",
            displayName: this.model.getDisplayName(),
            userColor: this.model.getUserColor(),
            select_merchant: this.model.hasMultipleMerchants(),
            isGettingMimicked: isMimicking(),
            canSeeDataStatus: store.getState().common.user_permissions.can_view_data_availability,
            ...this.getTopMerchantInfo()
        };
        
        // Compile partial and append to the main output
        return {
            ...data,
            activeMerchantInfoCont: `<div class="merchant-name">${pazienHistory.isAdminApp() ? "Administrator" : data.display_name || ""}</div>`
        };
    },
    
    showMerchantPicker(e = false) {
        if (e) e.preventDefault();
        Radio.channel("login").request("merchant:switch");
    },
    
    stopMimic(e) {
        e.preventDefault();
        store.dispatch(STOP_MIMIC_USER());
    },
    
    logout(e) {
        e.preventDefault();
        pazienHistory.navigate("logout", {
            trigger: true
        });
    },
    
    goToPage(e) {
        e.preventDefault();
        Radio.channel("main").request("navigate", e.currentTarget.dataset.fragment);
    },
    
    onDestroy() {
        if (this.entityView) {
            this.entityView.destroy();
            this.entityView = null;
        }
        
        $(document).off("keydown.topbar");
    }
});
